import type { ClinicSettings } from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';
import type { Json } from '../database.types';

export class SettingsService {
  async getAll(clinicId: string): Promise<ClinicSettings> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('settings')
      .select('key, value')
      .eq('clinic_id', clinicId);
    if (error) throw error;

    const settings: ClinicSettings = {};
    for (const row of data ?? []) {
      if (row.key === 'business_hours') settings.business_hours = row.value as ClinicSettings['business_hours'];
      if (row.key === 'appointment_settings') settings.appointment_settings = row.value as ClinicSettings['appointment_settings'];
      if (row.key === 'notifications') settings.notifications = row.value as ClinicSettings['notifications'];
      if (row.key === 'app_branding') settings.app_branding = row.value as ClinicSettings['app_branding'];
    }
    return settings;
  }

  async uploadAppLogo(clinicId: string, file: File): Promise<string> {
    const supabase = getSupabaseClient();
    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
    const path = `app-logos/${clinicId}/logo.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    const logoUrl = `${data.publicUrl}?t=${Date.now()}`;

    await this.upsert(clinicId, 'app_branding', { logo_url: logoUrl });
    return logoUrl;
  }

  async clearAppLogo(clinicId: string): Promise<void> {
    const supabase = getSupabaseClient();

    const { data: files } = await supabase.storage.from('avatars').list(`app-logos/${clinicId}`);
    if (files?.length) {
      const paths = files.map((f) => `app-logos/${clinicId}/${f.name}`);
      await supabase.storage.from('avatars').remove(paths);
    }

    await this.upsert(clinicId, 'app_branding', { logo_url: null });
  }

  async upsert(clinicId: string, key: string, value: unknown): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('settings')
      .upsert({ clinic_id: clinicId, key, value: value as Json }, { onConflict: 'clinic_id,key' });
    if (error) throw error;
  }
}

export const settingsService = new SettingsService();
